import {
  createSlice,
  createEntityAdapter,
  EntityState,
} from '@reduxjs/toolkit';
import { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from 'state/store';

type variant = 'information' | 'success' | 'error' | 'warning';

export type SnackBarNotification = {
  id: string;
  notificationVariant: variant;
  notificationTitle: string;
  notificationMessage?: string | ReactNode;
  timeout?: number;
  autoHide?: boolean;
};

export type SnackBarNotificationState = EntityState<SnackBarNotification>;

/* ============================= ENTITY ADAPTER ============================== */
export const adapter = createEntityAdapter<SnackBarNotification>({
  selectId: (notification) => notification.id,
});

/* ============================= INITIAL STATE ============================== */
const initialState: SnackBarNotificationState = adapter.getInitialState();

/* ================================= REDUCER ================================ */
const snackbarNotificationSlice = createSlice({
  name: 'snackbarNotification',
  initialState,
  reducers: {
    showNotification: (
      state,
      action: {
        payload: Omit<SnackBarNotification, 'id'>;
      }
    ) => {
      const {
        notificationMessage,
        notificationTitle,
        notificationVariant,
        autoHide = true,
        timeout,
      } = action.payload;

      const id = uuidv4();
      const newNotification: SnackBarNotification = {
        id,
        notificationVariant,
        notificationTitle,
        notificationMessage,
        timeout: autoHide ? timeout || 4000 : 0,
        autoHide,
      };

      adapter.addOne(state, newNotification);
    },
    hideNotification: (state, action: { payload: string }) => {
      adapter.removeOne(state, action.payload);
    },
  },
});

/* ================================ ACTIONS ================================= */
export const { hideNotification, showNotification } =
  snackbarNotificationSlice.actions;

/* =============================== SELECTORS ================================ */
const adapterSelectors = adapter.getSelectors(
  (state: RootState) => state.snackbarNotification
);

export const getAllNotifications = (state: RootState) => {
  return adapterSelectors.selectAll(state);
};

export const getSnackBarNotificationState = (state: RootState) =>
  adapterSelectors.selectTotal(state) > 0;

export default snackbarNotificationSlice.reducer;
